import React, { useState, useEffect } from 'react';
import { mockData } from '@/services/mockData';
import Header from '@/components/Header';

interface Bot {
  id: string;
  workspace_id: string;
  strategy: string;
  pair: string;
  mode: string;
  status: string;
  created_at: string;
}

function Bots() {
  const [bots, setBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingBot, setEditingBot] = useState<Bot | null>(null);
  const [newBot, setNewBot] = useState({
    strategy: 'default',
    pair: 'BTC-USD',
    mode: 'live'
  });

  useEffect(() => {
    // Simulate loading delay for better UX
    const timer = setTimeout(() => {
      // Convert mock data to match the Bot interface
      const mockBots: Bot[] = mockData.bots.map((bot, index) => ({
        id: bot.id,
        workspace_id: `workspace-${index + 1}`,
        strategy: bot.strategy,
        pair: bot.pair,
        mode: bot.mode,
        status: bot.status,
        created_at: bot.created
      }));
      setBots(mockBots);
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const createBot = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Create a new bot with mock data
    const newBotData: Bot = {
      id: `bot-${Date.now()}`,
      workspace_id: `workspace-${bots.length + 1}`,
      strategy: newBot.strategy,
      pair: newBot.pair,
      mode: newBot.mode,
      status: 'running',
      created_at: new Date().toISOString()
    };

    // Add to the list
    setBots([...bots, newBotData]);
    
    // Reset form
    setShowCreateForm(false);
    setNewBot({ strategy: 'default', pair: 'BTC-USD', mode: 'live' });
  };

  const editBot = (bot: Bot) => {
    setEditingBot(bot);
    setNewBot({
      strategy: bot.strategy,
      pair: bot.pair,
      mode: bot.mode
    });
  };

  const updateBot = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBot) return;

    const updatedBots = bots.map(bot => 
      bot.id === editingBot.id 
        ? { ...bot, ...newBot }
        : bot
    );
    
    setBots(updatedBots);
    setEditingBot(null);
    setNewBot({ strategy: 'default', pair: 'BTC-USD', mode: 'live' });
  };

  const deleteBot = (botId: string) => {
    if (window.confirm('Are you sure you want to delete this bot?')) {
      setBots(bots.filter(bot => bot.id !== botId));
    }
  };

  const cancelEdit = () => {
    setEditingBot(null);
    setNewBot({ strategy: 'default', pair: 'BTC-USD', mode: 'live' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="max-w-6xl mx-auto p-3 md:p-6">
        <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Trading Bots</h1>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {showCreateForm ? 'Cancel' : 'Create Bot'}
          </button>
        </div>

        {/* Create/Edit Bot Form */}
        {(showCreateForm || editingBot) && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              {editingBot ? 'Edit Bot' : 'Create New Bot'}
            </h2>
            <form onSubmit={editingBot ? updateBot : createBot} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Strategy</label>
                  <select
                    value={newBot.strategy}
                    onChange={(e) => setNewBot({ ...newBot, strategy: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="default">Default</option>
                    <option value="momentum">Momentum</option>
                    <option value="mean_reversion">Mean Reversion</option>
                    <option value="arbitrage">Arbitrage</option>
                    <option value="dca">Dollar Cost Average</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trading Pair</label>
                  <select
                    value={newBot.pair}
                    onChange={(e) => setNewBot({ ...newBot, pair: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="BTC-USD">BTC-USD</option>
                    <option value="ETH-USD">ETH-USD</option>
                    <option value="XRP-USD">XRP-USD</option>
                    <option value="ADA-USD">ADA-USD</option>
                    <option value="DOT-USD">DOT-USD</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mode</label>
                  <select
                    value={newBot.mode}
                    onChange={(e) => setNewBot({ ...newBot, mode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="live">Live</option>
                    <option value="paper">Paper Trading</option>
                    <option value="backtest">Backtest</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={editingBot ? cancelEdit : () => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  {editingBot ? 'Update Bot' : 'Create Bot'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Bots List */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bot ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Strategy</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pair</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {bots.map((bot) => (
                <tr key={bot.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {bot.id.slice(0, 8)}...
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                    {bot.strategy.replace('_', ' ')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                    {bot.pair}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                    {bot.mode}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      bot.status === 'running' || bot.status === 'active'
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {bot.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(bot.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => editBot(bot)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteBot(bot.id)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {bots.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No bots found. Create your first trading bot to get started.
            </div>
          )}
        </div>
      </div>

      {/* Strategy & Mode Info Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold mb-4 text-gray-900">Strategy & Mode Definitions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-md font-semibold mb-3 text-gray-800">Trading Strategies</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li><span className="font-medium text-gray-900">Scalping</span> – Small, frequent trades to capture quick profits</li>
              <li><span className="font-medium text-gray-900">Swing</span> – Medium-term trades held for days/weeks</li>
              <li><span className="font-medium text-gray-900">Trend</span> – Follows market momentum for longer gains</li>
              <li><span className="font-medium text-gray-900">Mean Reversion</span> – Trades against extreme price movements</li>
              <li><span className="font-medium text-gray-900">Arbitrage</span> – Exploits price differences across exchanges</li>
            </ul>
          </div>
          <div>
            <h3 className="text-md font-semibold mb-3 text-gray-800">Trading Modes</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li><span className="font-medium text-gray-900">Paper Trading</span> – Simulation only, no real money at risk</li>
              <li><span className="font-medium text-gray-900">Live Trading</span> – Executes real trades with your exchange account</li>
              <li><span className="font-medium text-gray-900">Backtest</span> – Historical simulation to test strategies</li>
              <li><span className="font-medium text-gray-900">Demo Mode</span> – Practice with virtual funds</li>
            </ul>
          </div>
        </div>
      </div>
        </div>
      </div>
    </>
  );
}

export default Bots;




